import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LayoutDashboard, Users, ClipboardList, FileBarChart, LogOut, UserCog, CalendarCheck, FilePen, Building2 } from "lucide-react";
import { useEffect, useState } from "react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!user) return children;

  const adminNav = [
    { to: "/", label: "Komuta Merkezi", icon: LayoutDashboard },
    { to: "/genel-puantaj", label: "Genel Puantaj", icon: CalendarCheck },
    { to: "/talepler", label: "Talepler", icon: FilePen },
    { to: "/personel", label: "Personel", icon: Users },
    { to: "/kullanicilar", label: "Şef & Kullanıcı", icon: UserCog },
    { to: "/oteller", label: "Oteller & Görseller", icon: Building2 },
    { to: "/raporlar", label: "Raporlar", icon: FileBarChart },
  ];
  const chefNav = [
    { to: "/puantaj", label: "Günlük Puantaj", icon: ClipboardList },
    { to: "/duzeltme-talebi", label: "Düzeltme Talebi Oluştur", icon: FilePen },
  ];
  const navs = user.role === "admin" ? adminNav : chefNav;

  const dateStr = now.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric", weekday: "long" });
  const timeStr = now.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <div className="min-h-screen jarvis-grid text-slate-200">
      {/* Top HUD bar */}
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-[#050b14]/80 border-b border-cyan-500/20 px-4 md:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full border-2 border-cyan-400 flex items-center justify-center"
                 style={{ boxShadow: "0 0 15px rgba(0,212,255,0.5)" }}>
              <span className="font-display text-cyan-300 font-black text-sm">J</span>
            </div>
            <div className="hidden sm:block">
              <div className="font-display font-black text-cyan-300 text-glow tracking-[0.2em] text-sm">J.A.R.V.I.S.</div>
              <div className="font-mono text-[9px] tracking-[0.25em] text-cyan-400/60 uppercase">Arsi Hotels</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {navs.map((n) => {
              const Icon = n.icon;
              const active = loc.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                  title={n.label}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg font-mono text-xs uppercase tracking-wider transition ${
                    active
                      ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/40"
                      : "text-slate-400 hover:text-cyan-300 hover:bg-cyan-500/5 border border-transparent"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className={user.role === "admin" ? "hidden 2xl:inline" : ""}>{n.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:flex flex-col items-end">
            <div className="font-mono text-xs text-cyan-300/90 tracking-wider">{dateStr}</div>
            <div className="font-mono text-lg text-cyan-200 text-glow-sm tabular-nums">{timeStr}</div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-mono text-[10px] tracking-widest text-emerald-300 uppercase hidden sm:inline">Sistem Aktif</span>
          </div>
          <div className="flex items-center gap-3 pl-4 border-l border-cyan-500/20">
            <div className="hidden sm:block text-right">
              <div className="text-sm text-slate-200 font-medium">{user.name}</div>
              <div className="font-mono text-[10px] text-cyan-400/70 uppercase tracking-wider">
                {user.role === "admin" ? "İK Yöneticisi" : "Departman Şefi"}
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); nav("/login"); }}
              className="h-9 w-9 rounded-lg border border-rose-500/30 hover:bg-rose-500/15 flex items-center justify-center text-rose-300"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile nav */}
      <nav className="md:hidden sticky top-[65px] z-30 flex gap-1 px-3 py-2 bg-[#050b14]/80 backdrop-blur border-b border-cyan-500/15 overflow-x-auto">
        {navs.map((n) => {
          const Icon = n.icon;
          const active = loc.pathname === n.to;
          return (
            <Link key={n.to} to={n.to} data-testid={`mnav-${n.to.replace("/", "")}`} ref={active ? (el) => el?.scrollIntoView({ inline: "center", block: "nearest" }) : undefined}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-mono text-[10px] uppercase whitespace-nowrap ${
                active ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" : "text-slate-400 border border-transparent"
              }`}>
              <Icon className="w-3.5 h-3.5" /> {n.label}
            </Link>
          );
        })}
      </nav>

      <main className="p-4 md:p-6">{children}</main>
    </div>
  );
}
